import { Funnel, ListBullets, MapTrifold } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import { formatNumber } from '@/lib/i18n/format'

import { useAppLocale } from '../hooks/ui'
import type { DiscoveryView } from '../types'
import { RefreshButton } from './refresh-button'

interface DiscoveryToolbarProps {
  resultCount: number
  view: DiscoveryView
  onViewChange: (view: DiscoveryView) => void
  refreshing: boolean
  onRefresh: () => void
  showRefresh: boolean
  activeFilters: number
  onOpenFilters: () => void
  /** Mobile search row: icon-only controls and no result counter. */
  iconOnly?: boolean
}

export function DiscoveryToolbar({
  resultCount,
  view,
  onViewChange,
  refreshing,
  onRefresh,
  showRefresh,
  activeFilters,
  onOpenFilters,
  iconOnly = false,
}: DiscoveryToolbarProps) {
  const { t } = useTranslation()
  const locale = useAppLocale()

  if (iconOnly) {
    return (
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          className="grid size-11 shrink-0 place-items-center rounded-full border border-line bg-white text-deep hover:bg-mint"
          onClick={() => onViewChange(view === 'list' ? 'map' : 'list')}
          aria-label={
            view === 'list'
              ? t('discovery.view.map')
              : t('discovery.view.list')
          }
          aria-pressed={view === 'map'}
        >
          {view === 'list' ? (
            <MapTrifold size={19} weight="bold" aria-hidden="true" />
          ) : (
            <ListBullets size={19} weight="bold" aria-hidden="true" />
          )}
        </button>

        {showRefresh ? (
          <RefreshButton onRefresh={onRefresh} refreshing={refreshing} />
        ) : null}

        <button
          type="button"
          className="relative grid size-11 shrink-0 place-items-center rounded-full border border-line bg-white text-deep hover:bg-mint"
          onClick={onOpenFilters}
          aria-label={
            activeFilters > 0
              ? t('discovery.filters.openWithCount', { count: activeFilters })
              : t('discovery.filters.open')
          }
        >
          <Funnel size={19} weight="bold" aria-hidden="true" />
          {activeFilters > 0 ? (
            <span className="absolute -top-1 -right-1 grid min-w-5 place-items-center rounded-full bg-mint px-1 text-[11px] font-bold text-teal ring-2 ring-paper">
              {formatNumber(activeFilters, locale)}
            </span>
          ) : null}
        </button>
      </div>
    )
  }

  return (
    <div className="mt-5 flex flex-wrap items-center gap-2">
      <p className="mr-auto text-sm font-semibold text-muted" role="status">
        {t('discovery.results', {
          count: resultCount,
          formatted: formatNumber(resultCount, locale),
        })}
      </p>

      <button
        type="button"
        className="inline-flex min-h-11 items-center gap-2 rounded-full border border-line bg-white px-3 text-sm font-bold hover:bg-mint"
        onClick={onOpenFilters}
        aria-label={
          activeFilters > 0
            ? t('discovery.filters.openWithCount', { count: activeFilters })
            : t('discovery.filters.open')
        }
      >
        <Funnel size={17} weight="bold" aria-hidden="true" className="text-teal" />
        {t('discovery.filters.button')}
        {activeFilters > 0 ? (
          <span className="grid min-w-5 place-items-center rounded-full bg-mint px-1 text-[11px] font-bold text-teal">
            {formatNumber(activeFilters, locale)}
          </span>
        ) : null}
      </button>

      <div
        className="inline-flex rounded-full border border-line bg-white p-1"
        role="group"
        aria-label={t('discovery.view.label')}
      >
        <button
          type="button"
          className={`view-toggle ${view === 'list' ? 'is-active' : ''}`}
          aria-pressed={view === 'list'}
          onClick={() => onViewChange('list')}
        >
          <ListBullets size={17} weight="bold" aria-hidden="true" />
          {t('discovery.view.list')}
        </button>
        <button
          type="button"
          className={`view-toggle ${view === 'map' ? 'is-active' : ''}`}
          aria-pressed={view === 'map'}
          onClick={() => onViewChange('map')}
        >
          <MapTrifold size={17} weight="bold" aria-hidden="true" />
          {t('discovery.view.map')}
        </button>
      </div>

      {showRefresh ? (
        <RefreshButton onRefresh={onRefresh} refreshing={refreshing} />
      ) : null}
    </div>
  )
}
